import moment from 'moment-timezone';
export var PaymentInterval;
(function (PaymentInterval) {
    PaymentInterval["Quarterly"] = "QUARTERLY";
    PaymentInterval["QuarterlyMonthAdvance"] = "QUARTERLY_MONTH_ADVANCE";
    PaymentInterval["Monthly"] = "MONTHLY";
    PaymentInterval["Yearly"] = "YEARLY";
})(PaymentInterval || (PaymentInterval = {}));
const isInInterval = (from, date, to) => moment(date).isSameOrAfter(from) && moment(date).isBefore(to);
export const getNextBillableSubscription = (tenantSubscripion) => {
    if (tenantSubscripion.invoiceLines.length === 0) {
        return null;
    }
    const startDate = moment
        .tz('Europe/Copenhagen')
        .add(1, 'month')
        .startOf('month');
    for (let i = 0; i < 60; i++) {
        const b = getBillableSubscriptions(tenantSubscripion, startDate);
        if (b) {
            return b;
        }
        startDate.add(1, 'month').startOf('month');
    }
    return null;
};
export const getBillableSubscriptions = (tenantSubscripion, date) => {
    let invoiceLines = [];
    let from = null;
    let to = null;
    for (let i = 0; i < tenantSubscripion.invoiceLines.length; i++) {
        const invoiceLine = tenantSubscripion.invoiceLines[i];
        switch (tenantSubscripion.paymentInterval) {
            case PaymentInterval.Monthly:
                from = moment(date).tz('Europe/Copenhagen').startOf('month');
                to = moment(date).tz('Europe/Copenhagen').endOf('month');
                if (!isInInterval(from, date, to)) {
                    continue;
                }
                invoiceLines = invoiceLines.concat(genSubscriptionInvoiceLines(invoiceLine, 1, from));
                break;
            case PaymentInterval.Quarterly:
                from = moment(date).tz('Europe/Copenhagen').startOf('quarter');
                to = moment(date).tz('Europe/Copenhagen').endOf('quarter');
                if (!isInInterval(from, date, from.clone().add(1, 'month'))) {
                    continue;
                }
                invoiceLines = invoiceLines.concat(genSubscriptionInvoiceLines(invoiceLine, 3, from));
                break;
            case PaymentInterval.QuarterlyMonthAdvance:
                from = moment(date)
                    .tz('Europe/Copenhagen')
                    .add(1, 'month')
                    .startOf('quarter');
                to = moment(date)
                    .tz('Europe/Copenhagen')
                    .add(1, 'month')
                    .endOf('quarter');
                if (!isInInterval(from.clone().subtract(1, 'month'), date, from)) {
                    continue;
                }
                invoiceLines = invoiceLines.concat(genSubscriptionInvoiceLines(invoiceLine, 3, from));
                break;
            case PaymentInterval.Yearly:
                from = moment(date).tz('Europe/Copenhagen').startOf('month');
                to = moment(date)
                    .tz('Europe/Copenhagen')
                    .startOf('month')
                    .add(1, 'year')
                    .subtract(1, 'millisecond');
                const startMonth = moment(tenantSubscripion.from)
                    .tz('Europe/Copenhagen')
                    .format('MM');
                if (startMonth !==
                    moment(date).tz('Europe/Copenhagen').startOf('month').format('MM')) {
                    continue;
                }
                invoiceLines = invoiceLines.concat(genSubscriptionInvoiceLines(invoiceLine, 12, from));
                break;
            default:
                throw new Error(`Payment interval (${tenantSubscripion.paymentInterval}) not supported`);
        }
    }
    if (invoiceLines.length === 0) {
        return null;
    }
    if (to?.isSameOrAfter(tenantSubscripion.to)) {
        return null;
    }
    if (from?.isBefore(tenantSubscripion.from)) {
        return null;
    }
    if (!from) {
        throw new Error('From date is missing');
    }
    if (!to) {
        throw new Error('To date is missing');
    }
    const t = {
        tenantSubscriptionId: tenantSubscripion.tenantSubscriptionId,
        tenantId: tenantSubscripion.tenantId,
        date: date.toISOString(),
        paymentInterval: tenantSubscripion.paymentInterval,
        period: {
            from: from.toISOString(),
            to: to.toISOString(),
        },
        lines: invoiceLines,
        invoiceText: tenantSubscripion.invoiceText,
        warning: tenantSubscripion.warning,
        showWarningAfter: tenantSubscripion.showWarningAfter,
    };
    return t;
};
const genSubscriptionInvoiceLines = (subscriptionInvoiceLine, months, from) => {
    let startOfPeriod = from.clone();
    return Array.from({ length: months }, (_) => {
        const m = {
            amount: subscriptionInvoiceLine.amountPerMonth,
            quantity: 1,
            sku: subscriptionInvoiceLine.sku,
            unit: subscriptionInvoiceLine.unit,
            from: startOfPeriod.tz('Europe/Copenhagen').toISOString(),
            to: startOfPeriod.tz('Europe/Copenhagen').endOf('month').toISOString(),
        };
        startOfPeriod = startOfPeriod
            .tz('Europe/Copenhagen')
            .add(1, 'month')
            .startOf('month');
        return m;
    });
};
export var InvoiceType;
(function (InvoiceType) {
    InvoiceType["Consumption"] = "CONSUMPTION";
    InvoiceType["License"] = "LICENSE";
    InvoiceType["Consultancy"] = "CONSULTANCY";
})(InvoiceType || (InvoiceType = {}));
export const organizeInvoiceLines = (invoiceLines) => Object.values(invoiceLines.reduce((acc, line) => {
    const crediting = line.amount < 0;
    const k = `${line.sku}${crediting ? '-crediting' : ''}${line.description || ''}`;
    if (!acc[k]) {
        acc[k] = {
            lines: [],
            amount: 0,
            quantity: 0,
            description: line.description,
            unit: line.unit,
            sku: line.sku,
            crediting,
        };
    }
    acc[k].lines = acc[k].lines.concat(line);
    acc[k].amount += line.amount;
    acc[k].quantity += line.quantity;
    return acc;
}, {}));
export var SKU;
(function (SKU) {
    SKU["DomesticSMS"] = "DOMESTIC_SMS";
    SKU["InternationalSMS"] = "INTERNATIONAL_SMS";
    SKU["DomesticAutoCall"] = "DOMESTIC_AUTO_CALL";
    SKU["InternationalAutoCall"] = "INTERNATIONAL_AUTO_CALL";
    SKU["DomesticLetter"] = "DOMESTIC_LETTER";
    SKU["InternationalLetter"] = "INTERNATIONAL_LETTER";
    SKU["ReturnLetter"] = "RETURN_LETTER";
    SKU["PhoneLookup"] = "PHONE_LOOKUP";
    SKU["EmailLookup"] = "EMAIL_LOOKUP";
    SKU["CaseCreation"] = "CASE_CREATION";
    SKU["MigratedCaseCreation"] = "MIGRATED_CASE_CREATION";
    SKU["MonthlyBaseFee"] = "MONTHLY_BASE_FEE";
    SKU["SupportAgreement"] = "SUPPORT_AGREEMENT";
    SKU["FreezingSystemSignOn"] = "FREEZING_SYSTEM_SIGN_ON";
    SKU["FreezingSystemSignOff"] = "FREEZING_SYSTEM_SIGN_OFF";
    SKU["DigitalSignature"] = "DIGITAL_SIGNATURE";
    SKU["EBoks"] = "EBOKS";
    SKU["MitID"] = "MITID";
    SKU["NemId"] = "NEMID";
    SKU["Consultancy"] = "CONSULTANCY";
})(SKU || (SKU = {}));
const STANDARD_CASE_FEE = 0;
export const billingPriceList = {
    [SKU.DomesticAutoCall]: 200,
    [SKU.InternationalAutoCall]: 200,
    [SKU.DomesticSMS]: 39,
    [SKU.InternationalSMS]: 100,
    [SKU.ReturnLetter]: 385,
    [SKU.DomesticLetter]: 1849,
    [SKU.InternationalLetter]: 4060,
    [SKU.DigitalSignature]: 1328,
    [SKU.PhoneLookup]: 43,
    [SKU.EmailLookup]: 12,
    [SKU.CaseCreation]: STANDARD_CASE_FEE,
    [SKU.MigratedCaseCreation]: STANDARD_CASE_FEE,
    [SKU.FreezingSystemSignOn]: 58,
    [SKU.FreezingSystemSignOff]: 58,
    [SKU.EBoks]: 296,
    [SKU.MitID]: 85,
    [SKU.NemId]: 195,
    [SKU.Consultancy]: 125000,
    [SKU.MonthlyBaseFee]: 0,
    [SKU.SupportAgreement]: 0,
};
//# sourceMappingURL=billing.js.map